import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import authService from '@/services/auth.service';
import { permissionsForRole } from '@/permissions/rolePermissions';
import {
  setToken,
  getToken,
  setStoredAdmin,
  getStoredAdmin,
  clearSession,
} from '@/utils/storage';

// Resolve a permission list for an admin: prefer server-sent permissions, else derive from role.
const resolvePermissions = (admin) =>
  admin?.permissions?.length ? admin.permissions : permissionsForRole(admin?.role);

export const login = createAsyncThunk('auth/login', async (credentials, { rejectWithValue }) => {
  try {
    const res = await authService.login(credentials);
    setToken(res.token);
    setStoredAdmin(res.admin);
    return res;
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const fetchMe = createAsyncThunk('auth/fetchMe', async (_, { rejectWithValue }) => {
  try {
    const res = await authService.me();
    setStoredAdmin(res.admin);
    return res.admin;
  } catch (err) {
    return rejectWithValue(err);
  }
});

export const logout = createAsyncThunk('auth/logout', async () => {
  try {
    await authService.logout();
  } catch {
    /* best-effort — clear locally regardless */
  }
  clearSession();
});

const storedAdmin = getStoredAdmin();

const initialState = {
  admin: storedAdmin,
  token: getToken(),
  permissions: resolvePermissions(storedAdmin),
  isAuthenticated: Boolean(getToken()),
  loading: false,
  error: null,
};

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    resetAuthError(state) {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, { payload }) => {
        state.loading = false;
        state.token = payload.token;
        state.admin = payload.admin;
        state.permissions = resolvePermissions(payload.admin);
        state.isAuthenticated = true;
      })
      .addCase(login.rejected, (state, { payload, error }) => {
        state.loading = false;
        state.error = payload || { message: error.message };
      })
      .addCase(fetchMe.fulfilled, (state, { payload }) => {
        state.admin = payload;
        state.permissions = resolvePermissions(payload);
        state.isAuthenticated = true;
      })
      .addCase(fetchMe.rejected, (state) => {
        state.isAuthenticated = false;
        state.admin = null;
        state.token = null;
        state.permissions = [];
      })
      .addCase(logout.fulfilled, (state) => {
        state.admin = null;
        state.token = null;
        state.permissions = [];
        state.isAuthenticated = false;
      });
  },
});

export const { resetAuthError } = authSlice.actions;
export default authSlice.reducer;
